import React, { useState, useEffect, useRef } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  Stack,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  CheckCircle,
  Work,
  PlayArrow,
  Pause,
  ExitToApp,
  Clear,
  DoneAll,
  FiberManualRecord,
} from '@mui/icons-material';
import api from '../../services/authService';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

// Helper function to format time ago (localized)
const formatTimeAgo = (date: string, t: (key: string) => string): string => {
  const now = new Date();
  const past = new Date(date);
  const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

  if (diffInSeconds < 60) return t('notifications.time.justNow');
  if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return t('notifications.time.minutesAgo').replace('{{count}}', String(minutes));
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return t('notifications.time.hoursAgo').replace('{{count}}', String(hours));
  }
  if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return t('notifications.time.daysAgo').replace('{{count}}', String(days));
  }
  const weeks = Math.floor(diffInSeconds / 604800);
  return t('notifications.time.weeksAgo').replace('{{count}}', String(weeks));
};

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  work_order_id: number | null;
  related_user_id: number | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  work_order?: {
    id: number;
    work_order_number: string;
    title: string;
  } | null;
  related_user?: {
    id: number;
    name: string;
    email: string;
  } | null;
}

const Notifications: React.FC = () => {
  const theme = useTheme();
  const { t } = useLanguage();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const open = Boolean(anchorEl);

  useEffect(() => {
    // Only fetch unread count on mount - full notifications list fetched when popover opens
    fetchUnreadCount();

    // Poll for unread count every 30 seconds (only fetch full list if popover is open)
    pollIntervalRef.current = setInterval(() => {
      fetchUnreadCount();
      if (open) {
        fetchNotifications();
      }
    }, 30000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [open]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/notifications');
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get('/notifications/unread-count');
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = async (id: number) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          is_read: true,
          read_at: new Date().toISOString(),
        }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleDelete = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      if (notifications.find((n) => n.id === id)?.is_read === false) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      handleMarkAsRead(notification.id);
    }
    if (notification.work_order_id) {
      navigate(`/work-orders/${notification.work_order_id}`);
      handleClose();
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'work_order_created':
      case 'work_order_assigned':
        return <Work />;
      case 'check_in':
        return <CheckCircle />;
      case 'check_out':
        return <ExitToApp />;
      case 'pause':
        return <Pause />;
      case 'resume':
        return <PlayArrow />;
      default:
        return <NotificationsIcon />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'work_order_created':
      case 'work_order_assigned':
        return theme.palette.primary.main;
      case 'check_in':
        return theme.palette.success.main;
      case 'check_out':
        return theme.palette.info.main;
      case 'pause':
        return theme.palette.warning.main;
      case 'resume':
        return theme.palette.success.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const getNotificationBgColor = (type: string) => {
    switch (type) {
      case 'work_order_created':
      case 'work_order_assigned':
        return theme.palette.primary.light + '20';
      case 'check_in':
        return theme.palette.success.light + '20';
      case 'check_out':
        return theme.palette.info.light + '20';
      case 'pause':
        return theme.palette.warning.light + '20';
      case 'resume':
        return theme.palette.success.light + '20';
      default:
        return theme.palette.grey[200] + '20';
    }
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        sx={{ mr: 1 }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { 
            width: 420, 
            maxHeight: 650, 
            mt: 1.5,
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          },
        }}
      >
        <Box>
          {/* Header */}
          <Box 
            sx={{ 
              p: 2.5, 
              pb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white',
              borderRadius: '8px 8px 0 0',
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                {t('notifications.title')}
              </Typography>
              {unreadCount > 0 && (
                <Tooltip title={t('notifications.actions.markAllRead')}>
                  <IconButton
                    size="small"
                    onClick={handleMarkAllAsRead}
                    sx={{ 
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                    }}
                  >
                    <DoneAll fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {unreadCount > 0 && (
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {t('notifications.summary.unreadCount')
                  .replace('{{count}}', String(unreadCount))}
              </Typography>
            )}
          </Box>

          {/* Content */}
          <Box sx={{ maxHeight: 550, overflow: 'auto' }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                <CircularProgress size={32} />
              </Box>
            ) : notifications.length === 0 ? (
              <Box 
                p={4} 
                textAlign="center"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: theme.palette.grey[200],
                    color: theme.palette.grey[400],
                  }}
                >
                  <NotificationsIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {t('notifications.empty.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('notifications.empty.subtitle')}
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1.5} sx={{ p: 2 }}>
                {notifications.map((notification) => {
                  const iconColor = getNotificationColor(notification.type);
                  const bgColor = getNotificationBgColor(notification.type);
                  
                  return (
                    <Card
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        border: notification.is_read 
                          ? `1px solid ${theme.palette.divider}` 
                          : `2px solid ${iconColor}`,
                        bgcolor: notification.is_read 
                          ? 'background.paper' 
                          : bgColor,
                        boxShadow: notification.is_read 
                          ? 'none' 
                          : `0 2px 8px ${iconColor}20`,
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 4px 12px ${iconColor}40`,
                        },
                        position: 'relative',
                        overflow: 'visible',
                      }}
                    >
                      {!notification.is_read && (
                        <FiberManualRecord
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            fontSize: 8,
                            color: iconColor,
                          }}
                        />
                      )}
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box display="flex" gap={2}>
                          {/* Icon Avatar */}
                          <Avatar
                            sx={{
                              bgcolor: iconColor,
                              width: 44,
                              height: 44,
                              flexShrink: 0,
                            }}
                          >
                            {getNotificationIcon(notification.type)}
                          </Avatar>

                          {/* Content */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: notification.is_read ? 500 : 700,
                                  fontSize: '0.95rem',
                                  lineHeight: 1.3,
                                  mb: 0.5,
                                }}
                              >
                                {notification.title}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={(e) => handleDelete(notification.id, e)}
                                sx={{
                                  ml: 1,
                                  opacity: 0.6,
                                  '&:hover': { opacity: 1, color: 'error.main' },
                                }}
                              >
                                <Clear fontSize="small" />
                              </IconButton>
                            </Box>
                            
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                fontSize: '0.875rem',
                                lineHeight: 1.5,
                                mb: 1,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {notification.message}
                            </Typography>

                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  fontSize: '0.75rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                {formatTimeAgo(notification.created_at, t)}
                              </Typography>
                              {notification.work_order_id && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.7rem',
                                    color: iconColor,
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                  }}
                                >
                                  View →
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default Notifications;
